import { useEffect, useState, type ReactElement } from 'react'
import {
  getFamilyWork,
  type GetFamilyWorkResult,
  type FamilyWorkItem,
} from '../../services/eduFamilyService'
import { FamilyCard, Button, StatusChip, type StatusPillTone } from '../Common'
import { mapEduServerError } from '../Education/classesBuilderHelpers'
import { useNls } from '../../nls/nlsProvider'
import { formatCalendarDate } from '../../utils/eduFamilyFormat'
import { FamilyHandInDialog } from './FamilyHandInDialog'
import { FamilyQuizDialog } from './FamilyQuizDialog'
import './familyWorkTab.css'

export interface FamilyWorkTabProps {
  workspaceId: string
  learnerId: string
  frozen?: boolean
}

const homeworkTone = (status: string): StatusPillTone => {
  switch (status) {
    case 'APPROVED':
      return 'success'
    case 'SUBMITTED':
      return 'info'
    case 'ASSIGNED':
    case 'RETURNED':
      return 'warning'
    case 'LATE':
      return 'danger'
    default:
      return 'neutral'
  }
}

export const FamilyWorkTab = ({
  workspaceId,
  learnerId,
  frozen = false,
}: FamilyWorkTabProps): ReactElement => {
  const { t, locale } = useNls()
  const [reloadSeq, setReloadSeq] = useState<number>(0)
  const queryKey = `${workspaceId}_${learnerId}_${reloadSeq}`

  const [fetchState, setFetchState] = useState<{
    key: string
    data: GetFamilyWorkResult | null
    error: string | null
  }>({
    key: '',
    data: null,
    error: null,
  })

  const [handInItem, setHandInItem] = useState<FamilyWorkItem | null>(null)
  const [quizItem, setQuizItem] = useState<FamilyWorkItem | null>(null)

  useEffect(() => {
    if (frozen) return
    let active = true

    getFamilyWork({ workspaceId, learnerId })
      .then((result) => {
        if (!active) return
        setFetchState({ key: queryKey, data: result, error: null })
      })
      .catch((err: unknown) => {
        if (!active) return
        setFetchState({ key: queryKey, data: null, error: mapEduServerError(err, t) })
      })

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, frozen, reloadSeq, queryKey, t])

  const error = fetchState.key === queryKey ? fetchState.error : null
  const loading = !frozen && fetchState.key !== queryKey && !error
  const data = fetchState.key === queryKey ? fetchState.data : null

  if (frozen) {
    return (
      <div className="familyWorkTab">
        <FamilyCard title={t('NLSFamilyNoticeHeading')}>
          <div className="familyWorkTab__frozen">{t('NLSFamilyFrozenNotice')}</div>
        </FamilyCard>
      </div>
    )
  }

  if (loading && !data) {
    return (
      <div className="familyWorkTab">
        <div className="familyWorkTab__loading" aria-label={t('NLSFamilyAriaLoading')}>
          {t('NLSFamilyLoading')}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="familyWorkTab">
        <FamilyCard title={t('NLSFamilyErrorTitle')}>
          <div className="familyWorkTab__error">{error}</div>
          <div className="familyWorkTab__retry">
            <Button variant="secondary" size="sm" onClick={() => setReloadSeq((s) => s + 1)}>
              {t('NLSFamilyRetry')}
            </Button>
          </div>
        </FamilyCard>
      </div>
    )
  }

  const workList = data?.work ?? []

  if (workList.length === 0) {
    return (
      <div className="familyWorkTab">
        <FamilyCard>
          <div className="familyWorkTab__empty">{t('NLSFamilyWorkEmpty')}</div>
        </FamilyCard>
      </div>
    )
  }

  const homeworkLabel = (status: string): string => {
    switch (status) {
      case 'ASSIGNED':
        return t('NLSEducationHomeworkStatusAssigned')
      case 'SUBMITTED':
        return t('NLSEducationHomeworkStatusSubmitted')
      case 'LATE':
        return t('NLSEducationHomeworkStatusLate')
      case 'RETURNED':
        return t('NLSEducationHomeworkStatusReturned')
      case 'APPROVED':
        return t('NLSEducationHomeworkStatusApproved')
      default:
        return ''
    }
  }

  return (
    <div className="familyWorkTab">
      <FamilyCard title={t('NLSFamilyWorkCardHeading')}>
        {workList.map((item) => {
          const isAssigned = item.status === 'ASSIGNED'
          const isReturned = item.status === 'RETURNED'
          const hasQuiz = Boolean(item.quiz)
          const isClosed = Boolean(item.quiz?.closed)
          const canHandIn = (isAssigned || isReturned) && !hasQuiz

          return (
            <div key={item.checklistId} className="familyWorkTab__row">
              <div className="familyWorkTab__rowMain">
                <div className="familyWorkTab__rowTitle">{item.title}</div>
                <div className="familyWorkTab__rowDue">
                  {formatCalendarDate(item.dueDate, locale)}
                </div>

                <div className="familyWorkTab__rowStatusLine">
                  {homeworkLabel(item.status) ? (
                    <StatusChip tone={homeworkTone(item.status)} size="sm">
                      {homeworkLabel(item.status)}
                    </StatusChip>
                  ) : null}

                  {item.status === 'APPROVED' && item.score != null && (
                    <span className="familyWorkTab__score">
                      {item.maxScore != null
                        ? t('NLSFamilyWorkScore', { score: item.score, maxScore: item.maxScore })
                        : t('NLSFamilyWorkScoreNoMax', { score: item.score })}
                    </span>
                  )}
                </div>

                {item.status === 'RETURNED' && item.feedback && (
                  <div className="familyWorkTab__feedback">{item.feedback}</div>
                )}

                {item.attachments && item.attachments.length > 0 && (
                  <div
                    className="familyWorkTab__attachments"
                    aria-label={t('NLSFamilyWorkAriaAttachments')}
                  >
                    {item.attachments.map((att) => (
                      <a
                        key={att.id}
                        href={att.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="familyWorkTab__attachmentLink"
                      >
                        {att.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {canHandIn && (
                <div className="familyWorkTab__rowAction">
                  <Button
                    variant={isReturned ? 'secondary' : 'primary'}
                    size="sm"
                    onClick={() => setHandInItem(item)}
                  >
                    {isReturned ? t('NLSFamilyWorkHandInAgain') : t('NLSFamilyWorkHandIn')}
                  </Button>
                </div>
              )}

              {hasQuiz && (
                <div className="familyWorkTab__rowAction">
                  <Button
                    variant={isClosed ? 'secondary' : 'primary'}
                    size="sm"
                    onClick={() => setQuizItem(item)}
                  >
                    {isClosed ? t('NLSFamilyWorkSeeAnswers') : t('NLSFamilyWorkStartQuiz')}
                  </Button>
                </div>
              )}
            </div>
          )
        })}
      </FamilyCard>

      {handInItem && (
        <FamilyHandInDialog
          open={true}
          workspaceId={workspaceId}
          checklistId={handInItem.checklistId}
          learnerId={learnerId}
          title={handInItem.title}
          onClose={() => setHandInItem(null)}
          onSuccess={() => {
            setHandInItem(null)
            setReloadSeq((s) => s + 1)
          }}
        />
      )}

      {quizItem && (
        <FamilyQuizDialog
          open={true}
          workspaceId={workspaceId}
          checklistId={quizItem.checklistId}
          learnerId={learnerId}
          title={quizItem.title}
          onClose={(attemptSubmitted?: boolean) => {
            setQuizItem(null)
            if (attemptSubmitted) {
              setReloadSeq((s) => s + 1)
            }
          }}
        />
      )}
    </div>
  )
}
